import React, { useMemo, useState } from 'react';
import { LaunchUniversal } from '@duxton-web/icons';
import { Form, Input, InputNumber, Select } from '@duxton-web/ui';
import { Rule } from '@duxton-web/ui/es/form';
import { useParams } from 'react-router-dom';

import { isViewMode } from '../../constants';
import { useExperimentContext } from '../../contexts/experiment-context';
import JsonEditorModal from '@/components/json-editor-modal';
import { VariableType } from '@/constants';
import { multipleOfToPrecisionConversion } from '@/features/variable/settings/validation/number-range';
import { VariableInfo } from '@/lib/PlannerDTOs';
import JsonEditor from '@/pages/ExperimentFormV2/common/JsonEditor';

const JsonEditModal = ({
  value,
  variableName,
  open,
  onCancel,
  onOk,
}: {
  value?: string;
  variableName?: string;
  open: boolean;
  onCancel?: () => void;
  onOk?: (jsString: string) => void;
}) => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { variables } = experimentContext;
  const variableInfo = useMemo(() => {
    return variables.find((v) => v.name === variableName);
  }, [variables, variableName]);
  const jsonSchema = variableInfo?.metadata?.jsonSchema;

  if (!open) return null;

  return (
    <JsonEditorModal
      data={value}
      readOnly={readOnly}
      jsonSchema={jsonSchema}
      onCancel={onCancel}
      onOk={onOk}
      // form.setFieldValue(
      //   ExpFormField.VARIABLES.TREATMENT_VALUE(
      //     showEditJsonModal.variableName,
      //     treatmentId,
      //   ),
      //   jsString,
      // );
    />
  );
};

const JsonEditorWrapper = ({
  value,
  status,
  variableName,
  onChange,
}: {
  value?: string;
  status?: 'error' | 'warning' | '';
  variableName?: string;
  onChange?: (jsString: string) => void;
}) => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <JsonEditor
        readOnly={readOnly}
        className={status}
        // className={isEmpty(variableName)}
        data={String(value)}
        actionButtonIcon={<LaunchUniversal />}
        onClickActionButton={() => {
          setOpen(true);
        }}
        onJsonChange={(jsString) => onChange(jsString)}
        height="150px"
        wrapEnabled
      />
      <JsonEditModal
        value={value}
        variableName={variableName}
        open={open}
        onCancel={() => {
          setOpen(false);
        }}
        onOk={(jsString) => {
          onChange(jsString);

          setOpen(false);
        }}
      />
    </>
  );
};

const TreatmentControl = ({
  configurationIndex,
  variableIndex,
  variableName,
  choiceIndex,
}: {
  configurationIndex: number;
  variableIndex: number;
  variableName: string;
  choiceIndex: number;
}) => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { variables } = experimentContext;
  const variableInfo = useMemo(() => {
    return variables.find((v) => v.name === variableName);
  }, [variables, variableName]);
  const variableType = variableInfo?.type;
  const jsonSchema = variableInfo?.metadata?.jsonSchema;
  const parsedJSONSchema = jsonSchema ? JSON.parse(jsonSchema) : null;
  const values = parsedJSONSchema?.enum;
  const form = Form.useFormInstance();

  const fieldName = [
    'configurations',
    configurationIndex,
    'variables',
    variableIndex,
    'choices',
    choiceIndex,
    [VariableType.FLOAT64, VariableType.INT64, VariableType.BOOLEAN].includes(
      variableType as VariableType,
    )
      ? 'value'
      : 'string',
  ] as const;

  const fieldValue = Form.useWatch(fieldName, { form });

  if (!variableInfo) return null;

  // Check if value is empty - distinguish between valid falsy values (0, false) and truly empty (null, undefined, empty string)
  const isEmpty = (value: any): boolean => {
    // null or undefined are always considered empty
    if (value === null || value === undefined) return true;

    // For numeric types (INT64, FLOAT64), 0 is a valid value, so only null/undefined are empty
    if (
      [VariableType.FLOAT64, VariableType.INT64].includes(
        variableType as VariableType,
      )
    ) {
      return false; // Already checked for null/undefined above
    }

    // For boolean, false (0) is a valid value, so only null/undefined are empty
    if (variableType === VariableType.BOOLEAN) {
      return false; // Already checked for null/undefined above
    }

    // For string types, empty string is considered empty
    if (variableType === VariableType.STRING) {
      return typeof value === 'string' && value.trim() === '';
    }

    // For JSON/MAP types, empty string might be valid JSON, but we'll consider it empty for validation
    if (
      [VariableType.JSON, VariableType.MAP].includes(
        variableType as VariableType,
      )
    ) {
      return (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '')
      );
    }

    return false;
  };

  let status: 'error' | 'warning' | '' =
    isEmpty(fieldValue) && !readOnly ? 'error' : '';

  let control = (
    <Input disabled={readOnly} style={{ width: '100%' }} status={status} />
  );

  if (values) {
    control = (
      <Select
        disabled={readOnly}
        options={values.map((value) => {
          return {
            value: value,
            label: value,
          };
        })}
        style={{ width: '100%' }}
        status={status}
      />
    );
  } else {
    switch (variableType) {
      case VariableType.STRING:
        control = (
          <Input
            disabled={readOnly}
            style={{ width: '100%' }}
            status={status}
          />
        );
        break;
      case VariableType.INT64:
        control = (
          <InputNumber
            disabled={readOnly}
            style={{ width: '100%' }}
            precision={0}
            type="number"
            status={status}
          />
        );

        break;
      case VariableType.FLOAT64:
        control = (
          <InputNumber
            disabled={readOnly}
            style={{ width: '100%' }}
            precision={multipleOfToPrecisionConversion(
              parsedJSONSchema?.multipleOf,
            )}
            type="number"
            status={status}
          />
        );
        break;
      case VariableType.BOOLEAN:
        control = (
          <Select
            disabled={readOnly}
            style={{ width: '100%' }}
            options={[
              { label: 'True', value: 1 },
              { label: 'False', value: 0 },
            ]}
            status={status}
          />
        );
        break;
      case VariableType.JSON:
      case VariableType.MAP:
        control = (
          <JsonEditorWrapper variableName={variableName} status={status} />
        );
    }
  }

  const getRules = (variable: VariableInfo) => {
    const rules: Rule[] = [
      {
        validator(_rule, _value, callback) {
          callback();
        },
      },
    ];

    const jsonSchema = variable?.metadata?.jsonSchema;
    const variableType = variable?.type;

    if (
      !jsonSchema ||
      [VariableType.BOOLEAN, VariableType.JSON, VariableType.MAP].includes(
        variableType as VariableType,
      )
    )
      return rules;

    const parsedJSONSchema = JSON.parse(jsonSchema) as any;
    const values = parsedJSONSchema?.enum;

    if (values) {
      rules.push({
        type: 'enum',
        enum: values,
      });

      return rules;
    }

    let min: number = null;
    let max: number = null;
    let errorMessage: string;

    if (variableType === VariableType.STRING) {
      min = parsedJSONSchema?.minLength;
      max = parsedJSONSchema?.maxLength;

      if (min != null && max != null)
        errorMessage = `Length of string must be between ${min} and ${max} characters`;
      else if (min != null)
        errorMessage = `Length of string must be no less than ${min} characters`;
      else if (max != null)
        errorMessage = `Length of string must be no longer than ${max} characters;`;
    } else {
      min = parsedJSONSchema?.minimum;
      max = parsedJSONSchema?.maximum;

      if (min != null && max != null)
        errorMessage = `Value must be between ${min} and ${max}`;
      else if (min != null) errorMessage = `Value cannot be lesser than ${min}`;
      else if (max != null)
        errorMessage = `Value cannot be greater than ${max}`;
    }

    rules.push({
      type: variableType === VariableType.STRING ? 'string' : 'number',
      min: min,
      max: max,
      message: errorMessage,
      transform: (value) => {
        switch (variableType) {
          case VariableType.INT64:
            return parseInt(value, 10);
          case VariableType.FLOAT64:
            return parseFloat(value);
          default:
            return value;
        }
      },
    });

    return rules;
  };

  return (
    <Form.Item
      name={[
        choiceIndex,
        [
          VariableType.FLOAT64,
          VariableType.INT64,
          VariableType.BOOLEAN,
        ].includes(variableType as VariableType)
          ? 'value'
          : 'string',
      ]}
      rules={getRules(variableInfo)}
      style={{ margin: 0 }}
    >
      {control}
    </Form.Item>
  );
};

export default TreatmentControl;
